import { useTranslation } from "react-i18next";
import type { MonthlySpend } from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatDate, formatNumber } from "@/i18n/format";
import { fillMonths, formatCost, monthDate } from "./spend";

const monthLabel = (month: string) => formatDate(monthDate(month), { month: "short", timeZone: "UTC" });

export function MonthlyTrend({ trend }: Readonly<{ trend: MonthlySpend[] }>) {
  const { t } = useTranslation("settings");
  const months = fillMonths(trend);
  const max = Math.max(0, ...months.map((m) => m.costUsd));
  return (
    <PageSection title={t("usage.trend.title")}>
      <div className="flex h-32 items-end gap-2" aria-hidden="true">
        {months.map((m) => (
          <div key={m.month} className="flex h-full min-w-0 flex-1 flex-col justify-end gap-1">
            <div
              title={t("usage.trend.tooltip", {
                month: monthLabel(m.month),
                cost: formatCost(m.costUsd),
                requests: formatNumber(m.requests),
              })}
              className="w-full rounded-t-sm bg-brand-gradient"
              style={{ height: max > 0 ? `${Math.max(2, (m.costUsd / max) * 100)}%` : "2px" }}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2" aria-hidden="true">
        {months.map((m) => (
          <Text key={m.month} as="span" variant="caption" tone="tertiary" className="min-w-0 flex-1 text-center">
            {monthLabel(m.month)}
          </Text>
        ))}
      </div>
      <table className="sr-only">
        <caption>{t("usage.trend.title")}</caption>
        <thead>
          <tr>
            <th scope="col">{t("usage.trend.month")}</th>
            <th scope="col">{t("usage.trend.cost")}</th>
            <th scope="col">{t("usage.trend.requests")}</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}>
              <td>{monthLabel(m.month)}</td>
              <td>{formatCost(m.costUsd)}</td>
              <td>{formatNumber(m.requests)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PageSection>
  );
}
